import type { ReactNode } from "react";
import { useMemo } from "react";

import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { serverResponseFormat } from "@similarweb/pro-logic-components";

import { SidePanelDatePicker } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/components/side-panel/SidePanelDatePicker";

interface IDatePickerNavigatorHook<T> {
    currentDateData: T;
    DatePicker: () => ReactNode;
}

interface IDatePickerNavigatorProps<T> {
    data: T[];
    currentDate: Dayjs;
    onDateChange: (date: Dayjs) => void;
}

export const useDatePickerPromptReportSidePanelNavigator = <T extends { date?: string | null }>({
    data,
    currentDate,
    onDateChange,
}: IDatePickerNavigatorProps<T>): IDatePickerNavigatorHook<T> => {
    const dataRecord = useMemo<Record<string, T> | undefined>(() => {
        if (!data) {
            return undefined;
        }

        return Object.fromEntries(data.map((response) => [response.date, response]));
    }, [data]);

    const currentDateData: T = dataRecord?.[currentDate.format(serverResponseFormat)];
    const datesWithData = useMemo<Dayjs[]>(
        () => (dataRecord ? Object.keys(dataRecord).map((dateStr) => dayjs.utc(dateStr)) : []),
        [dataRecord],
    );

    return {
        currentDateData,
        DatePicker: () => (
            <SidePanelDatePicker
                date={currentDate}
                onDateChange={onDateChange}
                supportedDates={datesWithData}
            />
        ),
    };
};
